
import { useState, useEffect } from 'react';
import { Crosshair } from 'lucide-react';
import { BookingData } from '../BookingWizard';
import LocationInput from '../../LocationInput';

interface RouteStepProps {
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
}

const RouteStep = ({ bookingData, onUpdate, onNext }: RouteStepProps) => {
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);

  // Set current date and time as default values when component mounts
  useEffect(() => {
    const now = new Date();
    
    // Format date as YYYY-MM-DD for input
    const today = now.toISOString().split('T')[0];
    
    // Format time as HH:MM for input
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;
    
    // Only set defaults if values are empty
    if (!bookingData.date) {
      onUpdate({ date: today });
    }
    
    if (!bookingData.time) {
      onUpdate({ time: currentTime });
    }
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: 'Current Location',
          };
          onUpdate({ pickup: location });
          setUseCurrentLocation(true);
        },
        (error) => {
          console.error('Error getting location:', error);
          setUseCurrentLocation(false);
        }
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (bookingData.pickup && bookingData.destination) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <LocationInput
          label="Pickup Address"
          value={bookingData.pickup?.address || ''}
          onChange={(location) => {
            onUpdate({ pickup: location });
            setUseCurrentLocation(false);
          }}
        />
        
        <button
          type="button"
          onClick={getCurrentLocation}
          className={`flex items-center ${
            useCurrentLocation ? 'text-primary hover:text-primary-dark' : 'text-primary hover:text-primary-dark'
          }`}
        >
          <Crosshair className="w-4 h-4 mr-2" />
          {useCurrentLocation ? 'Using current location' : 'Use my current location'}
        </button>

        <LocationInput
          label="Destination"
          value={bookingData.destination?.address || ''}
          onChange={(location) => onUpdate({ destination: location })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date
          </label>
          <input
            type="date"
            value={bookingData.date}
            onChange={(e) => onUpdate({ date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            min={new Date().toISOString().split('T')[0]}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time
          </label>
          <input
            type="time"
            value={bookingData.time}
            onChange={(e) => onUpdate({ time: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passengers
          </label>
          <select
            value={bookingData.passengers}
            onChange={(e) => onUpdate({ passengers: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
          >
            {[1, 2, 3, 4, 5, 6, 7].map((num) => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'passenger' : 'passengers'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Luggage
          </label>
          <select
            value={bookingData.luggage}
            onChange={(e) => onUpdate({ luggage: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
          >
            {[0, 1, 2, 3, 4].map((num) => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'piece' : 'pieces'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Flight/Train Number (optional)
        </label>
        <input
          type="text"
          value={bookingData.flightNumber || ''}
          onChange={(e) => onUpdate({ flightNumber: e.target.value })}
          placeholder="Ex: AB1234"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
        />
      </div>

      <div>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={bookingData.isRoundTrip}
            onChange={(e) => onUpdate({ isRoundTrip: e.target.checked })}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm text-gray-700">Round trip booking</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={!bookingData.pickup || !bookingData.destination}
        className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:bg-gray-400"
      >
        Continue
      </button>
    </form>
  );
};

export default RouteStep;
